import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowUpRight, ArrowDownRight, Settings, Search, Plus, Box, Layers, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Inventory() {
  const [transactions, setTransactions] = useState([]);
  const [components, setComponents] = useState([]);
  const [subAssemblies, setSubAssemblies] = useState([]);
  const [assemblies, setAssemblies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterType, setFilterType] = useState("");
  const [filterTransaction, setFilterTransaction] = useState("");
  
  const [formData, setFormData] = useState({
    item_id: "",
    item_type: "component",
    transaction_type: "stock_in",
    quantity: 1,
    notes: "",
  });

  useEffect(() => {
    fetchData();
  }, [filterType, filterTransaction]);

  const fetchData = async () => {
    try {
      const params = new URLSearchParams();
      if (filterType) params.append("item_type", filterType);
      if (filterTransaction) params.append("transaction_type", filterTransaction);
      params.append("limit", "100");

      const [transRes, compRes, saRes, aRes] = await Promise.all([
        axios.get(`${API}/inventory/transactions?${params}`),
        axios.get(`${API}/components`),
        axios.get(`${API}/sub-assemblies`),
        axios.get(`${API}/assemblies`)
      ]);

      setTransactions(transRes.data);
      setComponents(compRes.data);
      setSubAssemblies(saRes.data);
      setAssemblies(aRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to fetch inventory data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.item_id) {
      toast.error("Please select an item");
      return;
    }
    
    try {
      await axios.post(`${API}/inventory/transaction`, formData);
      toast.success("Transaction recorded successfully");
      setDialogOpen(false);
      setFormData({
        item_id: "",
        item_type: "component",
        transaction_type: "stock_in",
        quantity: 1,
        notes: "",
      });
      fetchData();
    } catch (error) {
      console.error("Error creating transaction:", error);
      toast.error(error.response?.data?.detail || "Failed to record transaction");
    }
  };

  const getItemOptions = () => {
    switch (formData.item_type) {
      case "component":
        return components;
      case "sub_assembly":
        return subAssemblies;
      case "assembly":
        return assemblies;
      default:
        return [];
    }
  };

  const getItemName = (itemId, itemType) => {
    let item;
    switch (itemType) {
      case "component":
        item = components.find(c => c.id === itemId);
        break;
      case "sub_assembly":
        item = subAssemblies.find(s => s.id === itemId);
        break;
      case "assembly":
        item = assemblies.find(a => a.id === itemId);
        break;
      default:
        return "Unknown";
    }
    return item ? item.name : "Unknown";
  };

  const getItemSku = (itemId, itemType) => {
    let item;
    switch (itemType) {
      case "component":
        item = components.find(c => c.id === itemId);
        break;
      case "sub_assembly":
        item = subAssemblies.find(s => s.id === itemId);
        break;
      case "assembly":
        item = assemblies.find(a => a.id === itemId);
        break;
      default:
        return "";
    }
    return item ? item.sku : "";
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case "stock_in":
        return <ArrowUpRight className="w-4 h-4 text-green-500" />;
      case "stock_out":
        return <ArrowDownRight className="w-4 h-4 text-red-500" />;
      default:
        return <Settings className="w-4 h-4 text-blue-500" />;
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "component":
        return <Box className="w-4 h-4" />;
      case "sub_assembly":
        return <Layers className="w-4 h-4" />;
      case "assembly":
        return <Package className="w-4 h-4" />;
      default:
        return null;
    }
  };

  return (
    <div data-testid="inventory-page">
      <div className="page-header flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="page-title">Inventory</h1>
          <p className="page-subtitle">Track stock movements and adjustments</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="rounded-none uppercase text-xs tracking-wider" data-testid="new-transaction-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Transaction
        </Button>
      </div>

      <div className="page-content">
        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <Select value={filterType || "all"} onValueChange={(v) => setFilterType(v === "all" ? "" : v)}>
            <SelectTrigger className="w-full md:w-[180px] rounded-none" data-testid="filter-type">
              <SelectValue placeholder="All Item Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Item Types</SelectItem>
              <SelectItem value="component">Components</SelectItem>
              <SelectItem value="sub_assembly">Sub-Assemblies</SelectItem>
              <SelectItem value="assembly">Assemblies</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filterTransaction || "all"} onValueChange={(v) => setFilterTransaction(v === "all" ? "" : v)}>
            <SelectTrigger className="w-full md:w-[180px] rounded-none" data-testid="filter-transaction">
              <SelectValue placeholder="All Transactions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Transactions</SelectItem>
              <SelectItem value="stock_in">Stock In</SelectItem>
              <SelectItem value="stock_out">Stock Out</SelectItem>
              <SelectItem value="adjustment">Adjustments</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Transactions Table */}
        <Card className="rounded-none border-zinc-200">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-muted-foreground">Loading transactions...</div>
            ) : transactions.length === 0 ? (
              <div className="p-8 text-center">
                <ArrowLeftRight className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No transactions found</p>
                <Button onClick={() => setDialogOpen(true)} variant="outline" className="mt-4 rounded-none">
                  Record your first transaction
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table" data-testid="transactions-table">
                  <thead>
                    <tr>
                      <th>Date & Time</th>
                      <th>Type</th>
                      <th>Item</th>
                      <th>Transaction</th>
                      <th className="text-right">Quantity</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((t) => (
                      <tr key={t.id} data-testid={`transaction-row-${t.id}`}>
                        <td className="whitespace-nowrap">{formatDate(t.created_at)}</td>
                        <td>
                          <div className="flex items-center gap-2">
                            {getTypeIcon(t.item_type)}
                            <Badge variant="outline" className="rounded-none text-xs uppercase">
                              {t.item_type.replace('_', ' ')}
                            </Badge>
                          </div>
                        </td>
                        <td>
                          <div>
                            <p className="font-medium font-sans">{getItemName(t.item_id, t.item_type)}</p>
                            <p className="text-xs text-muted-foreground font-mono">{getItemSku(t.item_id, t.item_type)}</p>
                          </div>
                        </td>
                        <td>
                          <div className="flex items-center gap-2">
                            {getTransactionIcon(t.transaction_type)}
                            <span className={`uppercase text-xs font-medium ${
                              t.transaction_type === 'stock_in' ? 'text-green-600' :
                              t.transaction_type === 'stock_out' ? 'text-red-600' : 'text-blue-600'
                            }`}>
                              {t.transaction_type.replace('_', ' ')}
                            </span>
                          </div>
                        </td>
                        <td className="text-right">
                          <span className={`font-mono font-semibold ${
                            t.transaction_type === 'stock_in' ? 'text-green-600' :
                            t.transaction_type === 'stock_out' ? 'text-red-600' : 'text-blue-600'
                          }`}>
                            {t.transaction_type === 'stock_in' ? '+' : t.transaction_type === 'stock_out' ? '-' : ''}
                            {t.quantity}
                          </span>
                        </td>
                        <td className="max-w-[200px] truncate text-muted-foreground font-sans text-sm">
                          {t.notes || "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* New Transaction Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md rounded-none" data-testid="transaction-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold uppercase tracking-wider">
              New Inventory Transaction
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label>Transaction Type</Label>
                <div className="grid grid-cols-3 gap-2">
                  <Button
                    type="button"
                    variant={formData.transaction_type === "stock_in" ? "default" : "outline"}
                    className="rounded-none"
                    onClick={() => setFormData({ ...formData, transaction_type: "stock_in" })}
                    data-testid="btn-stock-in"
                  >
                    <ArrowUpRight className="w-4 h-4 mr-1" />
                    Stock In
                  </Button>
                  <Button
                    type="button"
                    variant={formData.transaction_type === "stock_out" ? "default" : "outline"}
                    className="rounded-none"
                    onClick={() => setFormData({ ...formData, transaction_type: "stock_out" })}
                    data-testid="btn-stock-out"
                  >
                    <ArrowDownRight className="w-4 h-4 mr-1" />
                    Stock Out
                  </Button>
                  <Button
                    type="button"
                    variant={formData.transaction_type === "adjustment" ? "default" : "outline"}
                    className="rounded-none"
                    onClick={() => setFormData({ ...formData, transaction_type: "adjustment" })}
                    data-testid="btn-adjustment"
                  >
                    <Settings className="w-4 h-4 mr-1" />
                    Adjust
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Item Type</Label>
                <Select
                  value={formData.item_type}
                  onValueChange={(value) => setFormData({ ...formData, item_type: value, item_id: "" })}
                >
                  <SelectTrigger className="rounded-none" data-testid="select-item-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="component">Component</SelectItem>
                    <SelectItem value="sub_assembly">Sub-Assembly</SelectItem>
                    <SelectItem value="assembly">Assembly</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Select Item</Label>
                <Select
                  value={formData.item_id}
                  onValueChange={(value) => setFormData({ ...formData, item_id: value })}
                >
                  <SelectTrigger className="rounded-none" data-testid="select-item">
                    <SelectValue placeholder="Select an item" />
                  </SelectTrigger>
                  <SelectContent>
                    {getItemOptions().map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.name} ({item.sku}) - Qty: {item.quantity}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Quantity</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
                  className="rounded-none font-mono"
                  data-testid="input-quantity"
                />
                {formData.transaction_type === "adjustment" && (
                  <p className="text-xs text-muted-foreground">For adjustments, this sets the absolute quantity</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Notes (Optional)</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="rounded-none"
                  placeholder="Reason for transaction..."
                  rows={2}
                  data-testid="input-notes"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none">
                Cancel
              </Button>
              <Button type="submit" className="rounded-none uppercase tracking-wider" data-testid="submit-transaction-btn">
                Record Transaction
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Missing import fix
function ArrowLeftRight(props) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M8 3 4 7l4 4" />
      <path d="M4 7h16" />
      <path d="m16 21 4-4-4-4" />
      <path d="M20 17H4" />
    </svg>
  );
}
